/**
 * Page-specific copy for landing pages targeting low-Quality-Score Google Ads keywords.
 *
 * Each key maps to one keyword family. The matching SitePage route passes the
 * `pageContent` block via the `pageContent` prop and emits the route's own
 * FAQPage JSON-LD from the same `faqs` array (so the page text and the
 * structured data stay in sync).
 */

export type PageFaq = { q: string; a: string };

export type PageContent = {
  /** Visible H1 — must repeat the target query as literally as possible. */
  h1: string;
  /** Short eyebrow above the H1 (≤ 60 chars). */
  eyebrow: string;
  /** 2–3 short intro paragraphs answering the search intent. */
  intro: string[];
  /** 4–6 itinerary / inclusion / proof bullets. */
  bullets: string[];
  /** 3–5 keyword-specific FAQ entries (also serialized to FAQPage LD). */
  faqs: PageFaq[];
  /** WhatsApp pre-filled message for the section CTA. */
  whatsappMessage: string;
  /** Source label for the `whatsapp_click` GTM event. */
  whatsappSource: string;
};

type Bilingual = { en: PageContent; pt: PageContent };

/**
 * Keyed by stable identifier (NOT by URL) so EN and PT routes can share copy.
 */
export const landingContent: Record<string, Bilingual> = {
  algarveBoat: {
    en: {
      eyebrow: "Algarve Boat · Private Yacht · Vilamoura",
      h1: "Private Algarve boat charters from Vilamoura",
      intro: [
        "Looking for a boat in the Algarve? Morningstar is a 200 m² private yacht based in Marina de Vilamoura — fully crewed, fully catered, for up to 18 guests. We sail the central Algarve coast: Benagil Cave, the Marinha cliffs, the Vilamoura sunset coast and the Ria Formosa east of Faro.",
        "Every charter is private — only your group on board. No public group tours, no shared decks. Skipper, deckhand, fuel, drinks and Portuguese tapas always included.",
      ],
      bullets: [
        "Private 200 m² yacht — your group only, up to 18 guests",
        "Departure from Marina de Vilamoura (gate A2)",
        "Skipper, deckhand, fuel, drinks and tapas always included",
        "Half-day, sunset, Benagil Cave and full-day Algarve charters",
        "No licence needed — fully crewed",
      ],
      faqs: [
        {
          q: "How do I get a quote for an Algarve charter?",
          a: "Every charter on Morningstar is quoted per group, duration and route. Message us on WhatsApp with your dates and group size for a tailored quote — skipper, deckhand, fuel, drinks and Portuguese tapas are always included.",
        },
        {
          q: "Where do Algarve boat trips depart from?",
          a: "Morningstar is based at Marina de Vilamoura, gate A2. We sail east toward Faro and the Ria Formosa, or west toward Albufeira, Carvoeiro and the Benagil Cave.",
        },
        {
          q: "Do I need a licence to hire a boat in the Algarve?",
          a: "Not with us. Morningstar is a fully crewed private yacht — a licensed skipper and deckhand are always on board. You just relax.",
        },
        {
          q: "What's the best time of year to sail the Algarve?",
          a: "May to October offers the warmest sea and most stable weather. June and September are quieter and ideal for couples; July–August are peak season — book early.",
        },
      ],
      whatsappMessage: "Hello Morningstar, I'd like to charter a boat in the Algarve.",
      whatsappSource: "algarve_boat_section",
    },
    pt: {
      eyebrow: "Barco no Algarve · Iate Privado · Vilamoura",
      h1: "Charters privados de barco no Algarve a partir de Vilamoura",
      intro: [
        "À procura de um barco no Algarve? O Morningstar é um iate privado de 200 m² com base na Marina de Vilamoura — totalmente tripulado, com catering incluído, para até 18 pessoas. Navegamos a costa central do Algarve: Gruta de Benagil, falésias da Marinha, costa de Vilamoura ao pôr do sol e Ria Formosa a este de Faro.",
        "Todos os charters são privados — apenas o seu grupo a bordo. Sem tours em grupo, sem decks partilhados. Skipper, deckhand, combustível, bebidas e tapas portuguesas sempre incluídos.",
      ],
      bullets: [
        "Iate privado de 200 m² — apenas o seu grupo, até 18 pessoas",
        "Partida da Marina de Vilamoura (porta A2)",
        "Skipper, deckhand, combustível, bebidas e tapas sempre incluídos",
        "Meio-dia, pôr-do-sol, Gruta de Benagil e dia completo no Algarve",
        "Sem necessidade de carta — totalmente tripulado",
      ],
      faqs: [
        {
          q: "Como peço um orçamento para um charter no Algarve?",
          a: "Cada charter no Morningstar é cotado em função do grupo, duração e rota. Envie-nos uma mensagem no WhatsApp com a sua data e número de pessoas e enviamos um orçamento à medida — skipper, deckhand, combustível, bebidas e tapas portuguesas estão sempre incluídos.",
        },
        {
          q: "De onde partem os passeios de barco no Algarve?",
          a: "O Morningstar está na Marina de Vilamoura, porta A2. Navegamos para este em direção a Faro e à Ria Formosa, ou para oeste para Albufeira, Carvoeiro e a Gruta de Benagil.",
        },
        {
          q: "Preciso de carta para alugar um barco no Algarve?",
          a: "Connosco, não. O Morningstar é um iate privado totalmente tripulado — skipper licenciado e deckhand sempre a bordo. Só tem de relaxar.",
        },
        {
          q: "Qual a melhor altura para navegar no Algarve?",
          a: "De maio a outubro o mar está mais quente e o tempo mais estável. Junho e setembro são mais calmos e ideais para casais; julho e agosto são época alta — reserve com antecedência.",
        },
      ],
      whatsappMessage: "Olá Morningstar, gostaria de alugar um barco no Algarve.",
      whatsappSource: "algarve_boat_section",
    },
  },

  vilamouraBoatTrips: {
    en: {
      eyebrow: "Vilamoura Boat Trips · Marina de Vilamoura",
      h1: "Private Vilamoura boat trips on Morningstar",
      intro: [
        "Our Vilamoura boat trips run directly from Marina de Vilamoura on Morningstar — a 200 m² private yacht crewed by a licensed skipper and a deckhand. Every trip is private to your group, with up to 18 guests on board.",
        "Choose a half-day cliff cruise to Albufeira, a sunset cruise along the Vilamoura coast, a Benagil Cave day, or a full-day east toward the Ria Formosa islands. We bring the drinks, the tapas and the swim stops.",
      ],
      bullets: [
        "Departure from Marina de Vilamoura, gate A2 (5 min from Vilamoura centre)",
        "Private yacht — only your group, up to 18 guests",
        "Half-day (3h), sunset (2h) or full-day Algarve cruise (6–8h)",
        "Welcome drinks, Portuguese tapas and bottled water included",
        "Two swim stops and snorkelling gear on board",
        "Operated by RNAAT-licensed tour operator (Turismo de Portugal)",
      ],
      faqs: [
        {
          q: "How long are Vilamoura boat trips?",
          a: "We offer 2-hour sunset cruises, 3-hour half-day cliff cruises and 6–8 hour full-day Algarve charters. Most guests book the 3-hour or sunset slots.",
        },
        {
          q: "Where do boat trips leave from in Vilamoura?",
          a: "All trips depart from Marina de Vilamoura, gate A2 — a 5-minute walk from the Vilamoura casino and main beach.",
        },
        {
          q: "Can I do a Benagil Cave tour from Vilamoura?",
          a: "Yes. Our Benagil Cave day is a 6-hour cruise west from Vilamoura, passing the Marinha cliffs, with a small-tender visit inside the cave when sea state allows.",
        },
        {
          q: "Are Vilamoura boat trips suitable for kids?",
          a: "Absolutely. Morningstar has a shaded lounge, a marble bathroom and a swim platform. Life jackets in all sizes are on board.",
        },
      ],
      whatsappMessage: "Hello Morningstar, I'd like to book a Vilamoura boat trip.",
      whatsappSource: "vilamoura_boat_trips_section",
    },
    pt: {
      eyebrow: "Passeios de Barco em Vilamoura · Marina de Vilamoura",
      h1: "Passeios de barco privados em Vilamoura no Morningstar",
      intro: [
        "Os nossos passeios de barco em Vilamoura partem diretamente da Marina de Vilamoura no Morningstar — um iate privado de 200 m² tripulado por skipper licenciado e deckhand. Cada passeio é privado para o seu grupo, até 18 pessoas a bordo.",
        "Escolha um meio-dia pelas falésias até Albufeira, um cruzeiro pôr-do-sol pela costa de Vilamoura, um dia à Gruta de Benagil, ou um dia completo a este em direção à Ria Formosa. Bebidas, tapas e paragens para nadar incluídas.",
      ],
      bullets: [
        "Partida da Marina de Vilamoura, porta A2 (5 min do centro de Vilamoura)",
        "Iate privado — apenas o seu grupo, até 18 pessoas",
        "Meio-dia (3h), pôr-do-sol (2h) ou dia completo no Algarve (6–8h)",
        "Bebidas de boas-vindas, tapas portuguesas e água sempre incluídos",
        "Duas paragens para nadar e equipamento de snorkeling a bordo",
        "Operador licenciado RNAAT (Turismo de Portugal)",
      ],
      faqs: [
        {
          q: "Quanto tempo duram os passeios de barco em Vilamoura?",
          a: "Temos cruzeiros pôr-do-sol de 2 horas, meios-dias pelas falésias de 3 horas e dias completos no Algarve de 6–8 horas. A maioria reserva os de 3 horas ou pôr-do-sol.",
        },
        {
          q: "De onde partem os passeios de barco em Vilamoura?",
          a: "Todos os passeios partem da Marina de Vilamoura, porta A2 — a 5 minutos a pé do casino e da praia principal de Vilamoura.",
        },
        {
          q: "Posso ir à Gruta de Benagil a partir de Vilamoura?",
          a: "Sim. O nosso dia de Benagil é um cruzeiro de 6 horas para oeste, passando pelas falésias da Marinha, com visita em pequeno tender ao interior da gruta quando o mar permite.",
        },
        {
          q: "Os passeios são adequados para crianças?",
          a: "Sem dúvida. O Morningstar tem lounge sombreado, casa de banho em mármore e plataforma de banho. Coletes de salvação em todos os tamanhos a bordo.",
        },
      ],
      whatsappMessage: "Olá Morningstar, gostaria de reservar um passeio de barco em Vilamoura.",
      whatsappSource: "vilamoura_boat_trips_section",
    },
  },

  benagilCaveTour: {
    en: {
      eyebrow: "Benagil Cave Tour · Private Yacht from Vilamoura",
      h1: "Private Benagil Cave tour from Vilamoura",
      intro: [
        "A private Benagil Cave tour on Morningstar is a full Algarve day at sea: depart Marina de Vilamoura, sail west past Albufeira and the Marinha cliffs, and reach the iconic Benagil sea cave on your own private yacht.",
        "We anchor close to the cave and transfer guests by small tender for the cave entry (subject to sea state and the local maritime authority). After Benagil, lunch on board, a long swim stop in a hidden cove, and the cruise back along the cliffs at golden hour.",
      ],
      bullets: [
        "6-hour private charter from Marina de Vilamoura",
        "Cave entry by small tender (sea state permitting)",
        "Long swim stop with snorkelling at a quiet cliff cove",
        "Lunch on board: Portuguese tapas, fresh fruit, drinks",
        "Up to 18 guests — your group only, no shared decks",
        "Photo stop at the Marinha arches and Carvoeiro cliffs",
      ],
      faqs: [
        {
          q: "Can you go inside the Benagil Cave on a yacht?",
          a: "Morningstar anchors safely outside the cave and we transfer guests in a small tender to enter, or enters directly when sea conditions are favourable. Tender entry is subject to sea state and local maritime authority rules.",
        },
        {
          q: "How long is a Benagil Cave tour from Vilamoura?",
          a: "Our private Benagil day runs 4, 5, 7 or 10 hours: roughly 1h15 sailing each way.",
        },
        {
          q: "What's the best time of day for the Benagil Cave?",
          a: "Mid-morning departures hit Benagil with the sun directly over the famous oculus — best light for photos and calmest conditions. Afternoons are also fantastic, bringing the sunset on the way back to Vilamoura. The choice is yours!",
        },
        {
          q: "How do I get a quote for the Benagil Cave tour?",
          a: "Our private Benagil charter is quoted per group, date and duration. Message us on WhatsApp with your preferred date and number of guests — skipper, deckhand, fuel, drinks, tapas, tender and snorkelling gear are always included.",
        },
      ],
      whatsappMessage: "Hello Morningstar, I'd like to book a private Benagil Cave tour from Vilamoura.",
      whatsappSource: "benagil_cave_section",
    },
    pt: {
      eyebrow: "Gruta de Benagil · Iate Privado a partir de Vilamoura",
      h1: "Tour privado à Gruta de Benagil a partir de Vilamoura",
      intro: [
        "Um tour privado à Gruta de Benagil no Morningstar é um dia completo no mar do Algarve: partida da Marina de Vilamoura, navegação para oeste passando Albufeira e as falésias da Marinha, até à icónica gruta de Benagil no seu próprio iate.",
        "Fundeamos perto da gruta e transferimos os convidados em pequeno tender para a entrada na gruta (sujeito ao estado do mar e às regras da capitania local). Depois de Benagil, almoço a bordo, paragem longa para nadar numa enseada escondida e regresso ao golden hour pelas falésias.",
      ],
      bullets: [
        "Charter privado de 6 horas a partir da Marina de Vilamoura",
        "Entrada na gruta em pequeno tender (se o mar o permitir)",
        "Paragem longa para nadar e snorkeling numa enseada calma",
        "Almoço a bordo: tapas portuguesas, fruta fresca, bebidas",
        "Até 18 pessoas — apenas o seu grupo, decks privados",
        "Paragem fotográfica nos arcos da Marinha e falésias de Carvoeiro",
      ],
      faqs: [
        {
          q: "Pode-se entrar na Gruta de Benagil de iate?",
          a: "O Morningstar fundeia em segurança fora da gruta e transferimos os convidados em pequeno tender para entrar, ou entra diretamente quando as condições do mar são favoráveis. A entrada com tender está sujeita ao estado do mar e às regras da capitania.",
        },
        {
          q: "Quanto tempo dura um tour a Benagil a partir de Vilamoura?",
          a: "O nosso dia privado a Benagil é de 4, 5, 7 ou 10 horas: cerca de 1h15 de navegação em cada sentido.",
        },
        {
          q: "Qual a melhor hora do dia para visitar a Gruta de Benagil?",
          a: "Partidas a meio da manhã apanham Benagil com o sol diretamente sobre o famoso óculo — melhor luz para fotos e condições mais calmas. A tarde também é fantástica, pois traz o pôr-do-sol no regresso a Vilamoura. A escolha é sua!",
        },
        {
          q: "Como obtenho um orçamento para o tour à Gruta de Benagil?",
          a: "O nosso charter privado a Benagil é orçamentado por grupo, data e duração. Envie-nos uma mensagem no WhatsApp com a data pretendida e o número de convidados — skipper, deckhand, combustível, bebidas, tapas, tender e equipamento de snorkel estão sempre incluídos.",
        },
      ],
      whatsappMessage: "Olá Morningstar, gostaria de reservar um tour privado à Gruta de Benagil.",
      whatsappSource: "benagil_cave_section",
    },
  },

  boatHireAlgarve: {
    en: {
      eyebrow: "Boat Hire Algarve · Crewed Private Yacht",
      h1: "Boat hire in the Algarve — Morningstar private yacht",
      intro: [
        "Boat hire in the Algarve, done properly: Morningstar is a 200 m² private yacht based in Marina de Vilamoura, fully crewed and catered, for up to 18 guests. No licence needed, no shared decks, no surprise costs.",
        "You choose the slot — half-day cliff cruise, sunset along the Vilamoura coast, Benagil Cave day or full Algarve day east to the Ria Formosa. We handle skipper, deckhand, fuel, drinks and tapas. You bring the playlist.",
      ],
      bullets: [
        "Private 200 m² yacht for up to 18 guests",
        "Based at Marina de Vilamoura — central Algarve",
        "All-inclusive: crew, fuel, drinks, tapas, snorkelling gear",
        "Optional upgrades: DJ, saxophonist, photographer, cake",
        "5.0★ on Google and Tripadvisor (360+ reviews)",
        "RNAAT-licensed (Turismo de Portugal 743/2022)",
      ],
      faqs: [
        {
          q: "How do I get a quote for Algarve boat hire?",
          a: "Private boat hire on Morningstar is quoted per group and duration. Message us on WhatsApp with your dates and group size for a tailored quote — every charter includes skipper, deckhand, fuel, drinks and Portuguese tapas.",
        },
        {
          q: "Where in the Algarve can I hire a private boat?",
          a: "Morningstar is based at Marina de Vilamoura, central Algarve — equidistant from Faro Airport, Albufeira and Benagil. We sail the whole central Algarve coast.",
        },
        {
          q: "Do I need a boat licence to hire Morningstar?",
          a: "No. Morningstar is a fully crewed private yacht — a licensed skipper and deckhand are always on board, so guests don't need any qualification.",
        },
        {
          q: "How far in advance should I book Algarve boat hire?",
          a: "As soon as possible, really. We are experiencing constant high demand.",
        },
      ],
      whatsappMessage: "Hello Morningstar, I'd like to hire a private boat in the Algarve.",
      whatsappSource: "boat_hire_algarve_section",
    },
    pt: {
      eyebrow: "Aluguer de Barco no Algarve · Iate Privado Tripulado",
      h1: "Aluguer de barco no Algarve — iate privado Morningstar",
      intro: [
        "Aluguer de barco no Algarve, feito como deve ser: o Morningstar é um iate privado de 200 m² com base na Marina de Vilamoura, totalmente tripulado e com catering, para até 18 pessoas. Sem carta, sem decks partilhados, sem custos extra.",
        "Escolhe o horário — meio-dia pelas falésias, pôr-do-sol pela costa de Vilamoura, dia em Benagil ou dia completo a este até à Ria Formosa. Skipper, deckhand, combustível, bebidas e tapas por nossa conta. Tu trazes a playlist.",
      ],
      bullets: [
        "Iate privado de 200 m² para até 18 pessoas",
        "Base na Marina de Vilamoura — Algarve central",
        "Tudo incluído: tripulação, combustível, bebidas, tapas, snorkeling",
        "Extras opcionais: DJ, saxofonista, fotógrafo, bolo",
        "5.0★ no Google e Tripadvisor (360+ avaliações)",
        "Licenciado RNAAT (Turismo de Portugal 743/2022)",
      ],
      faqs: [
        {
          q: "Como peço um orçamento para alugar um barco no Algarve?",
          a: "O aluguer privado do Morningstar é cotado em função do grupo e da duração. Envie-nos uma mensagem no WhatsApp com a sua data e número de pessoas e enviamos um orçamento à medida — todos os charters incluem skipper, deckhand, combustível, bebidas e tapas portuguesas.",
        },
        {
          q: "Onde no Algarve posso alugar um barco privado?",
          a: "O Morningstar está na Marina de Vilamoura, no Algarve central — equidistante do Aeroporto de Faro, Albufeira e Benagil. Navegamos toda a costa central.",
        },
        {
          q: "Preciso de carta para alugar o Morningstar?",
          a: "Não. O Morningstar é um iate privado totalmente tripulado — skipper licenciado e deckhand sempre a bordo, os convidados não precisam de qualquer qualificação.",
        },
        {
          q: "Com quanta antecedência devo reservar?",
          a: "O mais cedo possível, mesmo. Estamos a registar uma procura elevada e constante.",
        },
      ],
      whatsappMessage: "Olá Morningstar, gostaria de alugar um barco privado no Algarve.",
      whatsappSource: "boat_hire_algarve_section",
    },
  },

  boatHireVilamoura: {
    en: {
      eyebrow: "Boat Hire Vilamoura · Marina de Vilamoura",
      h1: "Boat hire in Vilamoura — Morningstar private yacht",
      intro: [
        "Boat hire in Vilamoura starts at gate A2 of Marina de Vilamoura, where Morningstar — a 200 m² fully crewed private yacht — is moored. We hire only as a private charter: just your group, up to 18 guests, no shared trips.",
        "Pick a slot (half-day, sunset, Benagil, full day). We provide the skipper, deckhand, fuel, drinks and Portuguese tapas. No licence required, no hidden fees.",
      ],
      bullets: [
        "Marina de Vilamoura, gate A2 — 5 min from Vilamoura centre",
        "Private yacht — your group only, up to 18 guests",
        "All-inclusive crew, fuel, drinks and tapas",
        "Half-day, sunset, Benagil Cave and full-day Algarve options",
        "Add a DJ, saxophonist or photographer if you want",
      ],
      faqs: [
        {
          q: "How do I get a quote for boat hire in Vilamoura?",
          a: "Private hire on Morningstar is quoted per group, duration and route. Message us on WhatsApp with your dates and group size for a tailored quote — half-day, sunset, Benagil and full-day Algarve charters all include crew, fuel, drinks and tapas.",
        },
        {
          q: "Where exactly is the Vilamoura marina pickup?",
          a: "Morningstar is moored at Marina de Vilamoura, gate A2. The crew meets you at the pontoon 15 minutes before departure.",
        },
        {
          q: "Can I hire a boat in Vilamoura without a licence?",
          a: "Yes — Morningstar is fully crewed. A licensed skipper and deckhand are always on board, so no qualification is needed from guests.",
        },
        {
          q: "What's included in Vilamoura boat hire on Morningstar?",
          a: "Skipper, deckhand, fuel, soft drinks, Portuguese sparkling wine, beer, Portuguese tapas, bottled water, snorkelling gear, and the full yacht (sundeck, shaded lounge, marble bathroom, swim platform).",
        },
      ],
      whatsappMessage: "Hello Morningstar, I'd like to hire a boat in Vilamoura.",
      whatsappSource: "boat_hire_vilamoura_section",
    },
    pt: {
      eyebrow: "Aluguer de Barco em Vilamoura · Marina de Vilamoura",
      h1: "Aluguer de barco em Vilamoura — iate privado Morningstar",
      intro: [
        "O aluguer de barco em Vilamoura começa na porta A2 da Marina de Vilamoura, onde o Morningstar — um iate privado de 200 m² totalmente tripulado — está atracado. Alugamos apenas em modo privado: apenas o seu grupo, até 18 pessoas, sem viagens partilhadas.",
        "Escolha o horário (meio-dia, pôr-do-sol, Benagil, dia completo). Fornecemos skipper, deckhand, combustível, bebidas e tapas portuguesas. Sem carta, sem custos escondidos.",
      ],
      bullets: [
        "Marina de Vilamoura, porta A2 — 5 min do centro de Vilamoura",
        "Iate privado — apenas o seu grupo, até 18 pessoas",
        "Tripulação, combustível, bebidas e tapas incluídos",
        "Meio-dia, pôr-do-sol, Gruta de Benagil e dia completo",
        "Pode adicionar DJ, saxofonista ou fotógrafo",
      ],
      faqs: [
        {
          q: "Como peço um orçamento para alugar um barco em Vilamoura?",
          a: "O aluguer privado do Morningstar é cotado em função do grupo, duração e rota. Envie-nos uma mensagem no WhatsApp com a sua data e número de pessoas e enviamos um orçamento à medida — meio-dia, pôr-do-sol, Benagil e dia completo no Algarve incluem sempre tripulação, combustível, bebidas e tapas.",
        },
        {
          q: "Onde exatamente é o ponto de partida na marina?",
          a: "O Morningstar está atracado na Marina de Vilamoura, porta A2. A tripulação encontra-o no pontão 15 minutos antes da partida.",
        },
        {
          q: "Posso alugar um barco em Vilamoura sem carta?",
          a: "Sim — o Morningstar é totalmente tripulado. Skipper licenciado e deckhand sempre a bordo, os convidados não precisam de qualificação.",
        },
        {
          q: "O que está incluído no aluguer do Morningstar?",
          a: "Skipper, deckhand, combustível, refrigerantes, espumante português, cerveja, tapas portuguesas, água, equipamento de snorkeling, e o iate completo (sundeck, lounge sombreado, casa de banho em mármore, plataforma de banho).",
        },
      ],
      whatsappMessage: "Olá Morningstar, gostaria de alugar um barco em Vilamoura.",
      whatsappSource: "boat_hire_vilamoura_section",
    },
  },

  privateBoatRentalVilamoura: {
    en: {
      eyebrow: "Private Boat Rental · Vilamoura · Algarve",
      h1: "Private boat rental in Vilamoura aboard Morningstar",
      intro: [
        "Private boat rental in Vilamoura aboard Morningstar — a 200 m² yacht for up to 18 guests, moored at gate A2 of Marina de Vilamoura. Every rental is fully private: just your group, no shared trips, no public group tours.",
        "Pick a slot — half-day, sunset, Benagil Cave or a full Algarve day. Every private boat rental includes skipper, deckhand, fuel, soft drinks, Portuguese sparkling wine, beer, Portuguese tapas, bottled water and snorkelling gear. No licence required.",
      ],
      bullets: [
        "Marina de Vilamoura, gate A2 — 5 min from Vilamoura centre",
        "Private boat rental — your group only, up to 18 guests",
        "Skipper, deckhand, fuel, drinks and Portuguese tapas all included",
        "Half-day, sunset, Benagil Cave and full-day Algarve options",
        "Add a DJ, saxophonist or photographer on request",
      ],
      faqs: [
        {
          q: "How do I get a quote for a private boat rental in Vilamoura?",
          a: "Private boat rental on Morningstar is quoted per group, duration and route. Message us on WhatsApp with your dates and group size for a tailored quote — half-day, sunset, Benagil and full-day Algarve options all include crew, fuel, drinks and tapas.",
        },
        {
          q: "What's included in a private boat rental on Morningstar?",
          a: "Skipper, deckhand, fuel, soft drinks, Portuguese sparkling wine, beer, Portuguese tapas, bottled water, snorkelling gear, and the full yacht — sundeck, shaded lounge, marble bathroom and swim platform.",
        },
        {
          q: "Do I need a licence to rent a private boat in Vilamoura?",
          a: "No. Morningstar is fully crewed — a licensed skipper and deckhand are always on board, so no qualification is needed from guests.",
        },
        {
          q: "Where does the private boat rental depart from?",
          a: "Morningstar is moored at Marina de Vilamoura, gate A2. The crew meets you at the pontoon 15 minutes before departure — 5 minutes from Vilamoura centre.",
        },
      ],
      whatsappMessage: "Hello Morningstar, I'd like a private boat rental in Vilamoura.",
      whatsappSource: "private_boat_rental_vilamoura_section",
    },
    pt: {
      eyebrow: "Aluguer Privado de Barco · Vilamoura · Algarve",
      h1: "Aluguer privado de barco em Vilamoura no Morningstar",
      intro: [
        "Aluguer privado de barco em Vilamoura no Morningstar — um iate de 200 m² para até 18 pessoas, atracado na porta A2 da Marina de Vilamoura. Cada aluguer é totalmente privado: apenas o seu grupo, sem viagens partilhadas.",
        "Escolha o horário — meio-dia, pôr-do-sol, Gruta de Benagil ou dia completo no Algarve. Inclui sempre skipper, deckhand, combustível, bebidas e tapas portuguesas. Sem carta de marinheiro.",
      ],
      bullets: [
        "Marina de Vilamoura, porta A2 — 5 min do centro de Vilamoura",
        "Aluguer privado — apenas o seu grupo, até 18 pessoas",
        "Tripulação, combustível, bebidas e tapas incluídos",
        "Meio-dia, pôr-do-sol, Gruta de Benagil e dia completo",
        "Pode adicionar DJ, saxofonista ou fotógrafo",
      ],
      faqs: [
        {
          q: "Como peço um orçamento para um aluguer privado de barco em Vilamoura?",
          a: "O aluguer privado do Morningstar é cotado em função do grupo, duração e rota. Envie-nos uma mensagem no WhatsApp com a sua data e número de pessoas e enviamos um orçamento à medida.",
        },
        {
          q: "O que está incluído no aluguer privado?",
          a: "Skipper, deckhand, combustível, refrigerantes, espumante português, cerveja, tapas portuguesas, água, equipamento de snorkeling e o iate completo.",
        },
        {
          q: "Preciso de carta para alugar um barco privado em Vilamoura?",
          a: "Não — o Morningstar é totalmente tripulado. Skipper licenciado e deckhand sempre a bordo.",
        },
        {
          q: "Onde é o ponto de partida do aluguer?",
          a: "Marina de Vilamoura, porta A2. A tripulação encontra-o no pontão 15 minutos antes da partida.",
        },
      ],
      whatsappMessage: "Olá Morningstar, gostaria de um aluguer privado de barco em Vilamoura.",
      whatsappSource: "private_boat_rental_vilamoura_section",
    },
  },

  privateYachtVilamoura: {
    en: {
      eyebrow: "Private Yacht Charter · Vilamoura · Algarve",
      h1: "Private yacht charter from Vilamoura",
      intro: [
        "Morningstar is a 200 m² private yacht based at Marina de Vilamoura — fully crewed by a licensed skipper and deckhand, catered, and reserved exclusively for your group of up to 18 guests. No shared decks, no public groups.",
        "From the Vilamoura marina we reach the Falésia cliffs in minutes and the Marinha arches and Benagil Cave within a comfortable half-day. Choose a half-day cliff cruise, a sunset along the Vilamoura coast, a Benagil Cave day or a full Algarve day east toward the Ria Formosa.",
      ],
      bullets: [
        "Private 200 m² yacht — your group only, up to 18 guests",
        "Departure from Marina de Vilamoura, gate A2",
        "Skipper, deckhand, fuel, drinks and Portuguese tapas included",
        "Half-day, sunset, Benagil Cave and full-day Algarve charters",
        "Optional upgrades: DJ, saxophonist, photographer, cake",
        "RNAAT-licensed tour operator (Turismo de Portugal 743/2022)",
      ],
      faqs: [
        {
          q: "What does a private yacht charter from Vilamoura include?",
          a: "Skipper, deckhand, fuel, soft drinks, Portuguese sparkling wine, beer, Portuguese tapas, bottled water, snorkelling gear and the full use of the yacht (sundeck, shaded lounge, marble bathroom and swim platform).",
        },
        {
          q: "Where does the charter depart from?",
          a: "All charters depart from Marina de Vilamoura, gate A2 — a 5-minute walk from the Vilamoura casino and main beach. The crew meets you at the pontoon 15 minutes before departure.",
        },
        {
          q: "How many guests can join a private charter?",
          a: "Up to 18 guests on board. Every charter is private to your group — no shared decks, no other passengers.",
        },
        {
          q: "Do I need a boat licence?",
          a: "No. Morningstar is a fully crewed private yacht — a licensed skipper and deckhand are always on board, so guests don't need any qualification.",
        },
      ],
      whatsappMessage: "Hello Morningstar, I'd like to enquire about a private yacht charter from Vilamoura.",
      whatsappSource: "private_yacht_vilamoura_section",
    },
    pt: {
      eyebrow: "Charter de Iate Privado · Vilamoura · Algarve",
      h1: "Charter de iate privado a partir de Vilamoura",
      intro: [
        "O Morningstar é um iate privado de 200 m² com base na Marina de Vilamoura — totalmente tripulado por skipper licenciado e deckhand, com catering, e reservado exclusivamente para o seu grupo de até 18 pessoas. Sem decks partilhados, sem grupos públicos.",
        "A partir da Marina de Vilamoura chegamos às falésias da Falésia em minutos e aos arcos da Marinha e à Gruta de Benagil num meio-dia confortável. Escolha um meio-dia pelas falésias, um pôr-do-sol pela costa de Vilamoura, um dia em Benagil ou um dia completo a este até à Ria Formosa.",
      ],
      bullets: [
        "Iate privado de 200 m² — apenas o seu grupo, até 18 pessoas",
        "Partida da Marina de Vilamoura, porta A2",
        "Skipper, deckhand, combustível, bebidas e tapas portuguesas incluídos",
        "Meio-dia, pôr-do-sol, Gruta de Benagil e dia completo no Algarve",
        "Extras opcionais: DJ, saxofonista, fotógrafo, bolo",
        "Operador licenciado RNAAT (Turismo de Portugal 743/2022)",
      ],
      faqs: [
        {
          q: "O que está incluído num charter privado a partir de Vilamoura?",
          a: "Skipper, deckhand, combustível, refrigerantes, espumante português, cerveja, tapas portuguesas, água, equipamento de snorkeling e a utilização total do iate (sundeck, lounge sombreado, casa de banho em mármore e plataforma de banho).",
        },
        {
          q: "De onde parte o charter?",
          a: "Todos os charters partem da Marina de Vilamoura, porta A2 — a 5 minutos a pé do casino e da praia principal de Vilamoura. A tripulação encontra-o no pontão 15 minutos antes da partida.",
        },
        {
          q: "Quantas pessoas podem ir no charter privado?",
          a: "Até 18 pessoas a bordo. Cada charter é privado para o seu grupo — sem decks partilhados, sem outros passageiros.",
        },
        {
          q: "Preciso de carta de marinheiro?",
          a: "Não. O Morningstar é um iate privado totalmente tripulado — skipper licenciado e deckhand sempre a bordo, os convidados não precisam de qualquer qualificação.",
        },
      ],
      whatsappMessage: "Olá Morningstar, gostaria de informações sobre um charter de iate privado em Vilamoura.",
      whatsappSource: "private_yacht_vilamoura_section",
    },
  },

  sunsetCruiseVilamoura: {
    en: {
      eyebrow: "Sunset Cruise · Vilamoura · Algarve",
      h1: "Private Vilamoura sunset cruise on Morningstar",
      intro: [
        "Our Vilamoura sunset cruise is a private evening sail on Morningstar, a 200 m² yacht moored at Marina de Vilamoura. You leave the marina about two hours before sunset and follow the cliffs west toward Olhos d'Água and Falésia, with the Algarve coastline turning gold off the starboard side.",
        "On board: Portuguese sparkling wine, tapas, soft drinks and beer — served by your deckhand while the skipper picks the calmest anchorage for the light. Private to your group, up to 18 guests.",
      ],
      bullets: [
        "Private 2-hour evening cruise, timed to the sunset",
        "Departure from Marina de Vilamoura, gate A2",
        "Route along the Vilamoura coast, Olhos d'Água and Falésia cliffs",
        "Portuguese sparkling wine, tapas, soft drinks and beer included",
        "Up to 18 guests — your group only, no shared decks",
        "Skipper, deckhand, fuel and water always included",
      ],
      faqs: [
        {
          q: "What time does the Vilamoura sunset cruise depart?",
          a: "Departure is about 2 hours before sunset, so the time shifts through the season — roughly 19:00 in early summer and 17:30 in October. We confirm the exact slot when you book.",
        },
        {
          q: "Where does the sunset cruise sail to?",
          a: "We leave Marina de Vilamoura (gate A2) and follow the cliffs west toward Olhos d'Água and the Falésia coastline, anchoring for the sunset itself before cruising back to the marina.",
        },
        {
          q: "What's included on the sunset cruise?",
          a: "Skipper, deckhand, fuel, Portuguese sparkling wine, tapas, soft drinks, beer and bottled water — plus the full use of the yacht (sundeck, shaded lounge, marble bathroom and swim platform).",
        },
        {
          q: "Is the sunset cruise private?",
          a: "Yes — every charter is private. Only your group is on board, up to 18 guests. No shared decks, no other passengers.",
        },
      ],
      whatsappMessage: "Hello Morningstar, I'd like to enquire about a private sunset cruise from Vilamoura.",
      whatsappSource: "sunset_cruise_vilamoura_section",
    },
    pt: {
      eyebrow: "Cruzeiro ao Pôr-do-Sol · Vilamoura · Algarve",
      h1: "Cruzeiro privado ao pôr-do-sol em Vilamoura no Morningstar",
      intro: [
        "O nosso cruzeiro ao pôr-do-sol em Vilamoura é uma viagem privada ao entardecer no Morningstar, um iate de 200 m² atracado na Marina de Vilamoura. Sai-se da marina cerca de duas horas antes do pôr-do-sol e segue-se pelas falésias para oeste até Olhos d'Água e Falésia, com a costa do Algarve a ficar dourada a estibordo.",
        "A bordo: espumante português, tapas, refrigerantes e cerveja — servidos pela deckhand enquanto o skipper escolhe o fundeadouro mais calmo para a luz. Privado para o seu grupo, até 18 pessoas.",
      ],
      bullets: [
        "Cruzeiro privado de 2 horas ao entardecer, à hora do pôr-do-sol",
        "Partida da Marina de Vilamoura, porta A2",
        "Rota pela costa de Vilamoura, Olhos d'Água e falésias da Falésia",
        "Espumante português, tapas, refrigerantes e cerveja incluídos",
        "Até 18 pessoas — apenas o seu grupo, sem decks partilhados",
        "Skipper, deckhand, combustível e água sempre incluídos",
      ],
      faqs: [
        {
          q: "A que horas parte o cruzeiro ao pôr-do-sol em Vilamoura?",
          a: "A partida é cerca de 2 horas antes do pôr-do-sol, pelo que varia ao longo da época — aproximadamente 19:00 no início do verão e 17:30 em outubro. Confirmamos o horário exato na reserva.",
        },
        {
          q: "Por onde passa o cruzeiro ao pôr-do-sol?",
          a: "Saímos da Marina de Vilamoura (porta A2) e seguimos pelas falésias para oeste até Olhos d'Água e à costa da Falésia, fundeando para o pôr-do-sol antes do regresso à marina.",
        },
        {
          q: "O que está incluído no cruzeiro?",
          a: "Skipper, deckhand, combustível, espumante português, tapas, refrigerantes, cerveja e água — para além da utilização total do iate (sundeck, lounge sombreado, casa de banho em mármore e plataforma de banho).",
        },
        {
          q: "O cruzeiro é privado?",
          a: "Sim — todos os charters são privados. Apenas o seu grupo a bordo, até 18 pessoas. Sem decks partilhados, sem outros passageiros.",
        },
      ],
      whatsappMessage: "Olá Morningstar, gostaria de informações sobre um cruzeiro privado ao pôr-do-sol em Vilamoura.",
      whatsappSource: "sunset_cruise_vilamoura_section",
    },
  },
};

/** Build a FAQPage JSON-LD from a page-specific FAQ list. */
export function pageFaqLD(faqs: PageFaq[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}